// ログインの画面（<dialog>）と、短い知らせ。
import { signInWithGoogle } from "../auth";
import { track } from "../analytics";
import { t } from "../i18n";
import { h } from "./dom";

/** dismissible: false の窓は、右上の × を出さず、外側を押しても Esc でも閉じない（中のボタンでだけ閉じる） */
export function openDialog(
  build: (close: () => void) => HTMLElement[],
  label: string,
  extraClass = "",
  { dismissible = true }: { dismissible?: boolean } = {},
): Promise<void> {
  return new Promise((resolve) => {
    const dlg = h("dialog", { class: extraClass ? `dialog ${extraClass}` : "dialog", "aria-label": label });
    const close = () => dlg.close();
    if (dismissible) dlg.append(h("button", { class: "dialog-close", type: "button", "aria-label": t("common.close"), onclick: close }, "×"));
    dlg.append(...build(close));
    dlg.addEventListener("close", () => {
      dlg.remove();
      resolve();
    });
    if (dismissible) {
      // 外側を押したら閉じる
      dlg.addEventListener("click", (ev) => {
        if (ev.target === dlg) close();
      });
    } else {
      dlg.addEventListener("cancel", (ev) => ev.preventDefault());
    }
    // 全画面の表示の中でも見えるように、全画面の要素があればその中に出す
    (document.fullscreenElement ?? document.body).append(dlg);
    dlg.showModal();
  });
}

let signInOpen: Promise<void> | null = null;

/** ログインの画面を出す。閉じたら resolve する（ログインしたかどうかは session() で見る）。
 *  字は題名と 1 文だけにして、「Google で続ける」を窓の中でいちばん目立たせる（2026-10-25 ちさとさん
 *  「ログイン画面、文字が多い。もっとシンプルにアクションボタンを一番目立たせたい」）。
 *  名前やメールを見せないことだけは、小さく 1 行で残す */
export function openSignIn(): Promise<void> {
  signInOpen ??= openDialog((close) => {
    const err = h("p", { class: "form-error", role: "alert" });
    const google = h(
      "button",
      {
        class: "btn-google",
        type: "button",
        "data-testid": "signin-google",
        onclick: async () => {
          err.textContent = "";
          google.setAttribute("disabled", "");
          try {
            await signInWithGoogle();
            track("login", { method: "google" });
            close();
          } catch (e) {
            const reason = (e as { code?: string }).code ?? "";
            if (reason === "auth/popup-closed-by-user" || reason === "auth/cancelled-popup-request") return;
            err.textContent = reason === "auth/popup-blocked" ? t("auth.popupBlocked") : t("auth.failed");
            console.error("[auth]", e);
          } finally {
            google.removeAttribute("disabled");
          }
        },
      },
      // Google の「G」は、ピンクの地の上でも見えるように白い丸の中に置く
      h("span", { class: "g-mark", "aria-hidden": "true" }, "G"),
      h("span", { class: "ph" }, t("auth.google")),
    );
    return [
      // 開いたときは題名に focus を置く（右上の × に輪が出ないように。読み上げは題名から始まる）
      h("h2", { class: "signin-t ph", tabindex: "-1", autofocus: "" }, t("auth.title")),
      h("p", { class: "signin-lead ph" }, t("auth.lead")),
      google,
      err,
      h("p", { class: "signin-note ph" }, t("auth.private")),
      h("p", { class: "center" }, h("button", { class: "btn-text", type: "button", onclick: close }, t("auth.back"))),
    ];
  }, t("auth.signIn"), "signin-dialog").finally(() => {
    signInOpen = null;
  });
  return signInOpen;
}

export function toast(text: string) {
  const el = h("div", { class: "toast", role: "status" }, text);
  document.body.append(el);
  setTimeout(() => el.remove(), 4000);
}
